// client/src/components/auth/VerifyEmailPending.js
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import flex from '../../api/FlexFetch'
import './Auth.css'
import { APP_LOGO_SRC } from '../../config/branding'
import toastr from 'toastr'

function SmallIcon({ className = '' }) {
  return (
    <div className={`verify-email-icon mb-3 ${className}`} aria-hidden>
      <img
        src={APP_LOGO_SRC}
        alt="Serve Sync Tennis logo"
        className="auth-logo"
      />
    </div>
  )
}

function VerifyEmailPending() {
  const navigate = useNavigate()
  const { user, isLoggedIn, loading: authLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const mounted = useRef(true)

  // Check for verification token in URL
  const token = searchParams.get('token')

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    // If user is already verified, redirect to root/dashboard
    if (user && user.email_verified) {
      navigate('/')
      return
    }

    // If there's a verification token in URL, attempt verification
    if (token) {
      const handleEmailVerification = async (verificationToken) => {
        try {
          const response = await flex.flexFetch('/api/1.0/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token: verificationToken }),
          })

          const data = await response.json()

          if (!mounted.current) return

          if (response.ok) {
            toastr.success('Email verified successfully!')
            // Small delay so user can see toast
            setTimeout(() => navigate('/'), 1500)
          } else {
            const isExpired = data?.error?.details?.expired || data?.expired
            if (isExpired) {
              toastr.warning(
                'Verification link expired. Request a new one below.',
              )
            } else {
              const errMsg =
                data?.error?.message ||
                (typeof data?.error === 'string' ? data.error : null) ||
                'Failed to verify email'
              toastr.error(errMsg)
            }
          }
        } catch (err) {
          console.error('Email verification error:', err)
          if (!mounted.current) return
          toastr.error('Failed to verify email. Please try again.')
        }
      }

      handleEmailVerification(token)
    }
  }, [authLoading, isLoggedIn, user, token, navigate])

  // Cooldown timer effect (uses interval to avoid stale closures)
  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const id = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [resendCooldown])

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resending) return

    setResending(true)
    try {
      const response = await flex.flexFetch(
        '/api/1.0/auth/resend-verification',
        {
          method: 'POST',
        },
      )

      const data = await response.json()

      if (!mounted.current) return

      if (response.ok) {
        toastr.success('Verification email sent. Check your inbox.')
        setResendCooldown(15) // 15 seconds
      } else {
        const rateLimited =
          data?.error?.details?.rateLimited || data?.rateLimited
        const remainingMinutes =
          data?.error?.details?.remainingMinutes || data?.remainingMinutes
        if (rateLimited && remainingMinutes) {
          const remainingSeconds = remainingMinutes * 60
          setResendCooldown(remainingSeconds)
          toastr.warning(
            `Please wait ${remainingMinutes} minute(s) before trying again.`,
          )
        } else {
          const errMsg =
            data?.error?.message ||
            (typeof data?.error === 'string' ? data.error : null) ||
            'Failed to send verification email'
          toastr.error(errMsg)
        }
      }
    } catch (err) {
      console.error('Resend verification error:', err)
      if (!mounted.current) return
      toastr.error('Failed to send verification email. Please try again.')
    } finally {
      if (mounted.current) setResending(false)
    }
  }

  const handleSignOut = () => navigate('/sign-in')

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card auth-form text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="auth-container">
      <div className="auth-card auth-form">
        <div className="text-center mb-3">
          <h2 className="auth-title">Verify Your Email</h2>
          <SmallIcon name="bi-envelope-check text-primary" />
        </div>

        <div
          className="verify-email-panel"
          role="status"
          data-testid="verify-email-pending-panel"
        >
          <div className="verify-email-pill">Step 1 · Check your inbox</div>
          <h5 className="mb-1">We sent a verification email to</h5>
          <p className="verify-email-address mb-2">
            {user?.email || 'your email address'}
          </p>
          <p className="mb-0 text-muted">
            Open the email and click the link to verify your account and unlock
            Serve Sync.
          </p>
        </div>

        <div className="verify-email-steps">
          <div className="verify-section-title">Didn't receive the email?</div>
          <ul className="verify-checklist">
            <li>
              <i className="bi bi-shield-check"></i>
              Check your spam or junk folder
            </li>
            <li>
              <i className="bi bi-envelope-check"></i>
              Confirm you used the correct email address
            </li>
            <li>
              <i className="bi bi-clock-history"></i>
              Wait a few minutes — delivery can be delayed
            </li>
          </ul>
        </div>

        <div className="verify-email-actions">
          <button
            className="btn btn-outline-primary"
            data-testid="verify-email-resend-button"
            onClick={handleResendVerification}
            disabled={resending || resendCooldown > 0}
            aria-disabled={resending || resendCooldown > 0}
            aria-live="polite"
          >
            {resending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden
                ></span>
                Sending...
              </>
            ) : resendCooldown > 0 ? (
              `Resend available in ${formatTime(resendCooldown)}`
            ) : (
              'Resend Verification Email'
            )}
          </button>

          <button className="btn btn-link" onClick={handleSignOut}>
            Sign out and use a different account
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => navigate('/landing')}
          >
            Return to Home
          </button>
        </div>

        <div className="verify-support text-center">
          <div className="small text-muted">
            Having trouble? Contact support for assistance.
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPending
